import {
  createBeeswaxAccount,
  deleteBeeswaxPreset,
  getBeeswaxAccounts,
  getBeeswaxAdPosition,
  getBeeswaxAdvertisers,
  getBeeswaxDevices,
  getBeeswaxEnvironmentTypes,
  getBeeswaxInventorySources,
  getBeeswaxLineItem,
  getBeeswaxLineItemAudience,
  getBeeswaxLineItemsMetaData,
  getBeeswaxPresets,
  getBeeswaxPresetsById,
  getCities,
  getCountries,
  getRegions,
} from "@/apis/v1/beeswax";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { BeeswaxApiResponse } from "../LineItem/CreateOrEdit/Display/display.lineitem.interface";
import { BeeswaxLineItemAudiences } from "../Audience/audience.interface";

export const BEESWAX_ACCOUNTS_LIST_KEY = ["beeswax", "accounts"];
export const BEESWAX_ADVERTISERS_LIST_KEY = ["beeswax", "advertisers"];
export const COUNTRIES_KEY = ["beeswax", "countries"];
export const REGIONS_KEY = (search?: string) => ["beeswax", "regions", search];
export const REGIONS_RESOLVE_KEY = (keys?: string) => [
  "beeswax",
  "regions",
  "resolve",
  keys,
];
export const CITIES_KEY = (search?: string) => ["beeswax", "cities", search];
export const CITIES_RESOLVE_KEY = (geonames?: string) => [
  "beeswax",
  "cities",
  "resolve",
  geonames,
];
export const PRESETS_KEY = (
  type?: "campaign" | "line_item" | "targeting_expression",
  page?: number,
  size?: number,
  advertiserId?: number | null
) => ["beeswax", "presets", advertiserId, type, page, size];
export const PRESETS_ID_KEY = (id: string) => ["beeswax", "presets", id];

export const BEESWAX_INVENTORY_SOURCES = ["beeswax", "inventory-sources"];
export const BEESWAX_AD_POSITION = ["beeswax", "ad-position"];
export const BEESWAX_ENVIRONMENT_TYPES = ["beeswax", "environment-types"];
export const BEESWAX_DEVICES = ["beeswax", "devices"];

export function useBeeswaxAccountsList() {
  return useQuery(BEESWAX_ACCOUNTS_LIST_KEY, getBeeswaxAccounts);
}

export function useBeeswaxAdvertisersList(onSuccess?: () => void) {
  return useQuery(BEESWAX_ADVERTISERS_LIST_KEY, getBeeswaxAdvertisers, {
    onSuccess,
  });
}

export function useCreateBeeswaxAccount() {
  return useMutation(createBeeswaxAccount);
}

export function useBeeswaxLineItem({
  id,
  enabled = true,
  onSuccess,
  select,
  options = {},
}: {
  id: any;
  enabled?: boolean;
  onSuccess?: (data: any) => void;
  select?: (data: any) => any;
  options?: Record<any, any>;
}) {
  return useQuery<BeeswaxApiResponse>(["beeswax", "lineItem", id], getBeeswaxLineItem, {
    enabled,
    onSuccess,
    select,
    meta: {
      id,
    },
    ...options,
  });
}
export function useBeeswaxLineItemsMetaData({
  id,
  advertiserId,
  enabled = true,
  onSuccess,
  select,
}: {
  id: any;
  advertiserId: number | undefined;
  enabled?: boolean;
  onSuccess?: (data: any) => void;
  select?: (data: any) => any;
}) {
  return useQuery(
    ["beeswax", "lineItem", id, "metaData"],
    getBeeswaxLineItemsMetaData,
    {
      enabled,
      onSuccess,
      select,
      meta: {
        id,
        queryParams: {
          advertiserId,
        },
      },
    }
  );
}

export function useBeeswaxLineItemAudience({
  id,
  enabled = true,
  onSuccess,
}: {
  id: any;
  enabled?: boolean;
  onSuccess?: (data: any) => void;
}) {
  return useQuery<BeeswaxLineItemAudiences>(
    ["beeswax", "lineItem", id, "audience"],
    getBeeswaxLineItemAudience,
    {
      enabled,
      onSuccess,
      meta: {
        id,
      },
    }
  );
}
export function useCountries({
  enabled = true,
  queryParams,
}: {
  enabled?: boolean;
  queryParams?: {
    name: string;
  };
}) {
  return useQuery(COUNTRIES_KEY, getCountries, {
    enabled,
    meta: {
      queryParams,
    },
    staleTime: Infinity,
    // Backend currently returns every country twice (seeded a second time on
    // 2024-01-09). Dedupe by countryCode2 until that's fixed server-side.
    select: (data: any) => {
      if (!Array.isArray(data)) return data;
      const seen = new Set<string>();
      return data.filter((country: any) => {
        const key = country?.countryCode2;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
  });
}

// Beeswax /ref/regions returns { results: [{ region_code, key, region_name,
// country_code_2, ... }] }. Targeting value = `key` (e.g. "USA/TX").
function mapRegionOption(item: any) {
  return {
    label: item?.country_code_2
      ? `${item.region_name} (${item.country_code_2})`
      : item?.region_name,
    value: item?.key,
  };
}
// Beeswax /ref/cities returns { results: [{ id, name, region_code,
// region_name, country_code_2, geoname, ... }] }. Targeting value = `geoname`.
function mapCityOption(item: any) {
  const parts = [item?.name, item?.region_code, item?.country_code_2].filter(
    Boolean
  );
  return {
    label: parts.join(", "),
    value: item?.geoname,
  };
}

// Type-ahead search for regions (states). Enabled only when a search term is
// present, since the ref endpoint is search-driven.
export function useRegionSearch(search: string, enabled = true) {
  return useQuery(REGIONS_KEY(search), getRegions, {
    enabled: enabled && Boolean(search),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    meta: { queryParams: { search } },
    select: (data: any) => (data?.results ?? []).map(mapRegionOption),
  });
}

// Type-ahead search for cities.
export function useCitySearch(search: string, enabled = true) {
  return useQuery(CITIES_KEY(search), getCities, {
    enabled: enabled && Boolean(search),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    meta: { queryParams: { search } },
    select: (data: any) => (data?.results ?? []).map(mapCityOption),
  });
}

// Resolve already-selected region keys back to {label, value} for edit prefill.
// page_size is set to the number of requested keys so every selected value is
// returned in a single page (the ref endpoint otherwise defaults to ~50 rows).
export function useResolveRegions(keys: string[], enabled = true) {
  const list = keys ?? [];
  const joined = list.join(",");
  return useQuery(REGIONS_RESOLVE_KEY(joined), getRegions, {
    enabled: enabled && Boolean(joined),
    staleTime: Infinity,
    meta: { queryParams: { key__in: joined, page_size: list.length } },
    select: (data: any) => (data?.results ?? []).map(mapRegionOption),
  });
}

// Resolve already-selected city geonames back to {label, value} for edit prefill.
export function useResolveCities(geonames: string[], enabled = true) {
  const list = geonames ?? [];
  const joined = list.join(",");
  return useQuery(CITIES_RESOLVE_KEY(joined), getCities, {
    enabled: enabled && Boolean(joined),
    staleTime: Infinity,
    meta: { queryParams: { geoname__in: joined, page_size: list.length } },
    select: (data: any) => (data?.results ?? []).map(mapCityOption),
  });
}

export function useBeeswaxPresets<TDATA>({
  enabled = true,
  queryParams,
  onSuccess,
}: {
  enabled?: boolean;
  queryParams?: {
    type: "campaign" | "line_item" | "targeting_expression";
    page?: number;
    size?: number;
    advertiserId?: number | null;
  };
  onSuccess?: (data: any) => void;
}) {
  return useQuery<TDATA>(
    PRESETS_KEY(
      queryParams?.type,
      queryParams?.page,
      queryParams?.size,
      queryParams?.advertiserId
    ),
    getBeeswaxPresets,
    {
      enabled,
      meta: {
        queryParams,
      },
      onSuccess,
    }
  );
}
export function useBeeswaxPresetById<TDATA>({
  enabled = true,
  queryParams,
  onSuccess,
  id,
}: {
  enabled?: boolean;
  queryParams?: {
    favorite?: boolean;
  };
  id: string;
  onSuccess?: (data: any) => void;
}) {
  return useQuery<TDATA>(PRESETS_ID_KEY(id), getBeeswaxPresetsById, {
    enabled,
    meta: {
      id,
      queryParams,
    },
    onSuccess,
  });
}

export function useBeeswaxInventorySources(args?: {
  queryParams?: {
    id?: number;
    name?: string;
    key?: string;
    category?: string;
    viewable?: boolean;
    page_size?: number;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_INVENTORY_SOURCES, getBeeswaxInventorySources, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxAdPosition(args?: {
  queryParams?: {
    id?: number;
    name?: string;
    open_rtb_code?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_AD_POSITION, getBeeswaxAdPosition, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxEnvironmentTypes(args?: {
  queryParams?: {
    id?: number;
    name?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_ENVIRONMENT_TYPES, getBeeswaxEnvironmentTypes, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxDevices(args?: {
  queryParams?: {
    id?: number;
    name?: string;
  };
  options?: Record<any, any>;
}) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery(BEESWAX_DEVICES, getBeeswaxDevices, {
    meta: {
      queryParams: { ...queryParams },
    },
    ...options,
    staleTime: Infinity,
  });
}

export function useBeeswaxPresetDelete(args?: { options?: Record<any, any> }) {
  return useMutation(deleteBeeswaxPreset, args?.options);
}
